import { createContext, useContext, useMemo } from "react";
import type { DatePresetValue } from "#app/lib/utils/date-presets";
import { scopeFromEntity, type IssueScope } from "#app/lib/filters/issue-scope";

/**
 * The data window + entity facet a dashboard's widgets report on, provided by the
 * route through React context and read off it inside each widget's render. This is
 * the ONE coupling between a domain-free widget and "what it's about" — a widget is
 * never handed a scope as a prop.
 *
 * - `entity` — the analytics facet + single-entity widgets (`{type:"workflow",id}` …);
 *   `null` means "all" (e.g. the global Overview, or a catalog preview).
 * - `entityWhere` — the entity `where` a widget AND-merges into its own query (e.g.
 *   the pipeline / drilldown widgets narrowing to the view's scope).
 * - `dateRange` / `memberIds` / `onlyMe` — the universal data window the dashboard
 *   toolbar drives.
 *
 * A widget rendered OUTSIDE a dashboard (catalog preview, maximized widget) reads
 * `null` and falls back to a default window with no entity — never throws.
 */
export type DashboardScope = {
  entity: { type: string; id: string } | null;
  entityWhere: Record<string, unknown> | null;
  dateRange: DatePresetValue;
  memberIds: string[];
  onlyMe: boolean;
};

const DashboardScopeContext = createContext<DashboardScope | null>(null);

export const DashboardScopeProvider = DashboardScopeContext.Provider;

/** The surrounding dashboard's scope, or `null` when rendered outside a dashboard. */
export function useDashboardScope(): DashboardScope | null {
  return useContext(DashboardScopeContext);
}

/**
 * The surrounding dashboard's pinned entity as the issue screens' `IssueScope`,
 * or undefined off-scope and outside any dashboard. A widget's own filter
 * builder hands this to `AdvancedFilters` so its pickers offer only what the
 * dashboard's data can still be narrowed by.
 */
export function useDashboardIssueScope(): IssueScope | undefined {
  const entity = useDashboardScope()?.entity ?? null;
  const type = entity?.type;
  const id = entity?.id;
  return useMemo(
    () => (type && id ? scopeFromEntity({ type, id }) : undefined),
    [type, id],
  );
}
